import { warningText } from "../../engine/engineWarnings.ts";

/**
 * A missing token (`undefined` — column absent entirely) returns `fallback`
 * silently. A PRESENT token that doesn't parse to a finite number also
 * returns `fallback` (so the engine never computes on `NaN`, which would
 * silently poison every downstream calculation it touches), but additionally
 * records `context` in `warnings` so a real data-entry mistake surfaces
 * instead of vanishing.
 */
export function parseNum(
  token: string | undefined,
  fallback: number,
  context: string,
  warnings?: Set<string>
): number {
  if (token === undefined) return fallback;
  const n = Number(token);
  if (!Number.isFinite(n)) {
    warnings?.add(
      warningText(
        "engineWarning.invalidNumberFallback",
        `${context}: "${token}" is not a valid number — using ${fallback} instead.`,
        { context, token, fallback }
      )
    );
    return fallback;
  }
  return n;
}

/** Every element is parsed independently — one bad value only replaces (and warns about) that one element, not the whole array. */
export function parseNumArray(
  tokens: readonly string[],
  fallback: number,
  context: string,
  warnings?: Set<string>
): number[] {
  return tokens.map((token) => parseNum(token, fallback, context, warnings));
}
